import { action, Action, computed, Computed, thunk, Thunk } from 'easy-peasy';

import { Firebase } from 'services';
import {
  getIntegrations,
  hedgeCountAndPrebookAmount,
  hedgeCashflows,
  HedgeCashflowsParams,
  hedgeCashflowsByIds,
  HedgeCashflowsByIdsParams,
} from 'services/firebase';
import {
  refreshCashflowRisks,
  RefreshCashflowsRisksParams,
  subscribeToCashflowsRisks,
  SubscribeToCashflowsRisksParams,
} from 'services/firebase/analysis';
import {
  ICountry,
  IFees,
  ICurrency,
  IFundingAccount,
  IPotentialSavings,
  IPotentialSavingsWithDate,
  IRatings,
  ICurrencyNews,
  TBusinessNatureUnion,
  SWIFT_TYPE,
  Nullable,
  IRateAnalyses,
} from 'types';
import { CashflowsRisksData } from 'types/analyses';
import { Notify } from 'utils';

export interface ReferenceDataStateModel {
  isRefreshingCashflowsRisksData: boolean;
  nonTradingDays: string[];
  countries: ICountry[];
  currencyNews: ICurrencyNews[];
  fees: IFees[];
  fundingAccounts: IFundingAccount[];
  cashflowsRisks: CashflowsRisksData | null;
  isCashflowsRisksLoaded: boolean;
  systemVariables: {
    calculatedAmountDiffThreshold: number;
    standardTransferCharge: number;
    depositFeeDiffThreshold: number;
    prebookDefaultAmount: number;
    prebookMinimumAmount: number;
    priorityTransferFeeAmount: number;
    rateRelativeDiffThreshold: number;
    shortIdAlphabet: number;
    shortIdLength: number;
    tradingEnabled: boolean;
    transferDefaultAmount: number;
    transferMinimumAmount: number;
    sessionTimeout: number;
    prebookDaysBuffer: number;
    defaultBuyCurrency: string;
    defaultSellCurrency: string;
    invoicePrebookCostAmountThreshold: number;
  } | null;
  naturesOfBusiness: TBusinessNatureUnion[];
  rateAnalyses: IRateAnalyses | null;
  potentialSavings: IPotentialSavings | null;
  potentialSavingsWithDate: IPotentialSavingsWithDate | null;
  ratings: IRatings | null;
  setState: Action<ReferenceDataStateModel, [string, any]>;
  getNonTradingDays: Thunk<
    ReferenceDataStateModel,
    Firebase.GetNonTradingDaysParams
  >;
  getNaturesOfBusiness: Thunk<ReferenceDataStateModel>;
  getSystemVariables: Thunk<ReferenceDataStateModel>;
  getCountries: Thunk<ReferenceDataStateModel>;
  getCurrencyNews: Thunk<ReferenceDataStateModel>;
  getSwiftFees: Thunk<ReferenceDataStateModel>;
  getRateAnalyses: Thunk<
    ReferenceDataStateModel,
    Firebase.GetRateAnalysisParams
  >;
  getPotentialSavings: Thunk<
    ReferenceDataStateModel,
    Firebase.GetPotentialSavingsParams
  >;
  getPotentialSavingsWithDate: Thunk<
    ReferenceDataStateModel,
    Firebase.GetPotentialSavingsWithDateParams
  >;
  getRatings: Thunk<ReferenceDataStateModel, Firebase.GetRatingsParams>;
  countryByCode: Computed<
    ReferenceDataStateModel,
    (countryCode: Nullable<ICountry['alpha2']>) => ICountry | null
  >;
  feesByCurrencyCode: Computed<
    ReferenceDataStateModel,
    (currencyCode: Nullable<ICurrency['code']>) => IFees | null
  >;
  getSwiftShared: Computed<
    ReferenceDataStateModel,
    (currencyCode: Nullable<ICurrency['code']>) => IFees['swiftShared'] | null
  >;
  getSwiftOurs: Computed<
    ReferenceDataStateModel,
    (
      currencyCode: Nullable<ICurrency['code']>,
      country: Nullable<ICountry['alpha2']>
    ) => number | null
  >;
  getPriorityFee: Computed<
    ReferenceDataStateModel,
    (
      type: SWIFT_TYPE,
      currencyCode: Nullable<ICurrency['code']>,
      country: Nullable<ICountry['alpha2']>
    ) => number | null
  >;
  getFundingAccounts: Thunk<ReferenceDataStateModel>;
  fundingAccountByCurrency: Computed<
    ReferenceDataStateModel,
    (ccCode: ICurrency['code'] | undefined) => IFundingAccount | undefined
  >;
  currencyNewsByCurrency: Computed<
    ReferenceDataStateModel,
    (currencyCode: ICurrency['code']) => ICurrencyNews | null
  >;
  subscribeToCashflowsRisks: Thunk<
    ReferenceDataStateModel,
    Omit<SubscribeToCashflowsRisksParams, 'callback'>,
    null,
    object,
    (() => void) | undefined
  >;
  refreshCashflowsRisksData: Thunk<
    ReferenceDataStateModel,
    RefreshCashflowsRisksParams
  >;
  prebookAll: Thunk<ReferenceDataStateModel, HedgeCashflowsParams>;
  prebookAllCount: Thunk<ReferenceDataStateModel, HedgeCashflowsParams>;
  prebookAllByIds: Thunk<ReferenceDataStateModel, HedgeCashflowsByIdsParams>;
  getIntegrations: Thunk<ReferenceDataStateModel>;
  xeroImportPeriodData: any[];
  getRiskContribution: Thunk<ReferenceDataStateModel>;
  riskContribution: Record<string, Record<string, number>>;
}

export const ReferenceDataState: ReferenceDataStateModel = {
  isRefreshingCashflowsRisksData: false,
  xeroImportPeriodData: [],
  nonTradingDays: [],
  countries: [],
  currencyNews: [],
  fees: [],
  naturesOfBusiness: [],
  fundingAccounts: [],
  riskContribution: {},
  cashflowsRisks: null,
  isCashflowsRisksLoaded: false,
  systemVariables: null,
  rateAnalyses: null,
  potentialSavings: null,
  potentialSavingsWithDate: null,
  ratings: null,
  setState: action((state, payload) => {
    const [prop, to] = payload;
    state[prop] = to;
  }),
  getNonTradingDays: thunk(async (actions, params) => {
    const data = await Firebase.getNonTradingDays(params);

    if (data) {
      actions.setState(['nonTradingDays', data.data]);
    }
  }),
  getNaturesOfBusiness: thunk(async (actions) => {
    const data = await Firebase.getNatureOfBusiness();

    if (data) {
      // @ts-ignore
      actions.setState(['naturesOfBusiness', data.values]);
    }
  }),
  getSystemVariables: thunk(async (actions) => {
    const data = await Firebase.getSystemVariables();

    if (data) {
      // @ts-ignore
      actions.setState(['systemVariables', data]);
    }
  }),
  getCountries: thunk(async (actions) => {
    const data = await Firebase.getCountries();

    if (data) {
      actions.setState(['countries', data]);
    }
  }),
  getCurrencyNews: thunk(async (actions) => {
    try {
      const data = await Firebase.getCurrencyNews();

      if (data) {
        // @ts-ignore
        actions.setState(['currencyNews', data]);
      }
    } catch (error) {
      Notify.error(error.message);
    }
  }),
  getSwiftFees: thunk(async (actions) => {
    try {
      const data = await Firebase.getSwiftFees();

      if (data) {
        actions.setState(['fees', data]);
      }
    } catch (error) {
      console.warn(error);
    }
  }),
  countryByCode: computed(
    [(state) => state.countries],
    (countries) => (countryCode) =>
      countries.find(
        (item) => item.alpha2.toLowerCase() === countryCode?.toLowerCase()
      ) || null
  ),
  feesByCurrencyCode: computed(
    [(state) => state.fees],
    (fees) => (currencyCode) =>
      fees.find((item) => item.id === currencyCode) || null
  ),
  getSwiftShared: computed((state) => (currencyCode) =>
    state.feesByCurrencyCode(currencyCode)?.swiftShared || null
  ),
  getSwiftOurs: computed((state) => (currencyCode, country) => {
    if (currencyCode && country) {
      return (
        state.feesByCurrencyCode(currencyCode)?.swiftOurs?.[
          country.toUpperCase()
        ] || null
      );
    }

    return null;
  }),
  getPriorityFee: computed((state) => (type, currencyCode, country) =>
    type === SWIFT_TYPE.ours
      ? state.getSwiftOurs(currencyCode, country)
      : state.getSwiftShared(currencyCode)
  ),
  getRateAnalyses: thunk(async (actions, payload) => {
    const data = await Firebase.getRateAnalysis(payload);

    if (data) {
      actions.setState(['rateAnalyses', data]);
    }
  }),

  getPotentialSavings: thunk(async (actions, payload) => {
    const data = await Firebase.getPotentialSavings(payload);

    if (data) {
      actions.setState(['potentialSavings', data]);
    }
  }),
  getPotentialSavingsWithDate: thunk(async (actions, payload) => {
    const data = await Firebase.getPotentialSavingsWithDate(payload);

    if (data) {
      actions.setState(['potentialSavingsWithDate', data]);
    }
  }),
  getRatings: thunk(async (actions, payload) => {
    const data = await Firebase.getRatings(payload);

    if (data) {
      actions.setState(['ratings', data]);
    }
  }),
  getFundingAccounts: thunk(async (actions) => {
    const data = await Firebase.getFundingAccounts();

    if (data) {
      actions.setState(['fundingAccounts', data]);
    }
  }),
  fundingAccountByCurrency: computed(
    [(state) => state.fundingAccounts],
    (fundingAccounts) => (ccCode) =>
      fundingAccounts.find((item) => item.id === ccCode)
  ),
  currencyNewsByCurrency: computed(
    [(state) => state.currencyNews],
    (currencyNews) => (currencyCode) =>
      currencyNews.find(
        (item) => item.id.toLowerCase() === currencyCode?.toLowerCase()
      ) || null
  ),
  subscribeToCashflowsRisks: thunk(({ setState }, payload) => {
    const subscriber = subscribeToCashflowsRisks({
      ...payload,
      callback: (cashflowRisks) => {
        setState(['cashflowsRisks', cashflowRisks]);
        setState(['isCashflowsRisksLoaded', true]);
      },
    });

    return subscriber;
  }),
  refreshCashflowsRisksData: thunk(
    async ({ setState }, payload, { getState }) => {
      try {
        const { isRefreshingCashflowsRisksData } = getState();

        if (isRefreshingCashflowsRisksData) {
          return;
        }

        setState(['isRefreshingCashflowsRisksData', true]);

        await refreshCashflowRisks(payload);
      } catch (error) {
        console.log(`refreshCashflowsRisksData error: ${error}`);
      } finally {
        setState(['isRefreshingCashflowsRisksData', false]);
      }
    }
  ),
  prebookAll: thunk(async (_, payload) => {
    try {
      await hedgeCashflows(payload);
    } catch (error) {
      console.warn(error);
    }
  }),
  prebookAllCount: thunk(async (_, payload) => {
    return hedgeCountAndPrebookAmount(payload);
  }),
  prebookAllByIds: thunk(async (_, payload) => {
    try {
      const { data } = await hedgeCashflowsByIds(payload);

      if (data?.success) {
        const failureCount = data?.data?.failures?.length;
        const successCount = data?.data?.successes?.length;
        const totalCount = failureCount + successCount;

        if (failureCount > 0) {
          Notify.info(
            `We were able to prebook successfully for ${successCount} invoices out of ${totalCount}.${' '}
            ${failureCount} invoices unsuccessful. You can try again. If the issue persists, please contact support.`
          );
        } else {
          Notify.success(
            'A prebooking has been applied to all selected invoices'
          );
        }
      } else {
        Notify.error(`The action was unsuccessful. Error ${data?.message}`);
      }
    } catch (error) {
      Notify.error(error?.message);
    }
  }),
  getIntegrations: thunk(async (actions) => {
    try {
      const integrations = await getIntegrations();

      actions.setState(['integrations', integrations]);
    } catch (error) {
      console.warn(error);
    }
  }),
  getRiskContribution: thunk(async (actions) => {
    const data = await Firebase.getRiskContribution();

    if (data) {
      actions.setState(['riskContribution', data]);
    }
  }),
};
